import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import toast from 'react-hot-toast';
import Layout from '../../../../../components/Layout';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import { TransactionDetailsModal } from '../../../../../components/Admin/Payments/EFT/TransactionDetailsModal';
import PayoutTableSkeleton from '../../../../../components/SkeletonEffect/PayoutTableSkeleton/PayoutTableSkeleton';
import { axiosInstance, getErrorDetail } from '../../../../../utils/tokenUtils';
import TransactionDetailsIcon from '../../../../../assets/Admin/Payments/transaction-details-icon.svg';
import { toLocalTime } from '../../../../../utils/dateUtils';
import './EFTDetails.css';

const STATUS_STYLE = {
  pending: { color: '#E0AD14', bgcolor: '#FFFCF2', label: 'Pending' },
  transferred: { color: '#14B334', bgcolor: '#F2FAF3', label: 'Transferred' },
  failed: { color: '#E14949', bgcolor: '#FFF2F2', label: 'Failed' },
};

const SOURCE_LABEL = {
  student_session_payment: 'Session Payment',
  instructor_enrollment_fee: 'Enrollment Fee',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * API timestamps are naive UTC ("2026-10-26T08:42:00", no "Z"). new Date()
 * would read that as local time and show the UTC clock, so parse as UTC and
 * convert to the viewer's timezone.
 */
const formatTimestamp = (iso, format) => {
  const date = iso && toLocalTime(iso);
  return date && date.isValid() ? date.format(format) : '-';
};

const formatDate = iso => formatTimestamp(iso, 'DD MMM YYYY');

const formatDateTime = iso => formatTimestamp(iso, 'DD MMM YYYY, hh:mm A');

/**
 * A calendar date the admin picked (the Transaction Date). Stored as that
 * day's midnight, so it is shown as-is: converting it would move the day for
 * anyone west of UTC.
 */
const formatCalendarDate = iso => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const formatAmount = value =>
  Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
};

const EFTDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsCount, setItemsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isItemsLoading, setIsItemsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [rbacMe, setRbacMe] = useState(null);

  const fetchDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(`/admin/eft/transfers/${id}`);
      setDetail(data);
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to load the transfer'));
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchItems = useCallback(async () => {
    try {
      setIsItemsLoading(true);
      const { data } = await axiosInstance.get(`/admin/eft/transfers/${id}/items`, {
        params: {
          search_query: debouncedSearch?.trim() || undefined,
          page,
          page_size: pageSize,
        },
      });
      setItems(data?.results || []);
      setItemsCount(Number(data?.count || 0));
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to load payments'));
    } finally {
      setIsItemsLoading(false);
    }
  }, [id, debouncedSearch, page, pageSize]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      let cachedData = null;
      try {
        const cached = sessionStorage.getItem('rbac_admin_me');
        if (cached) {
          cachedData = JSON.parse(cached);
          setRbacMe(cachedData);
        }
      } catch (_) {}

      try {
        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);
        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!cachedData) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  const hasPermission = permission =>
    Array.isArray(rbacMe?.permissions) && rbacMe.permissions.includes(permission);
  // Admins hold these through their role; a finance officer only holds
  // delete_eft once it has been granted to them individually in RBAC.
  const canManageEft = hasPermission('manage_eft');
  const canDeleteEft = hasPermission('delete_eft');

  const status = STATUS_STYLE[detail?.status] || STATUS_STYLE.pending;
  const totalPages = Math.max(1, Math.ceil(itemsCount / pageSize));

  const paginationItems = useMemo(() => {
    const result = [];
    if (totalPages <= 1) return result;
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i += 1) result.push(i);
      return result;
    }
    result.push(1);
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) result.push('...');
    for (let i = start; i <= end; i += 1) result.push(i);
    if (end < totalPages - 1) result.push('...');
    result.push(totalPages);
    return result;
  }, [totalPages, page]);

  const handleDownload = async () => {
    const toastId = toast.loading('Preparing download...');
    try {
      setIsDownloading(true);
      const { data } = await axiosInstance.get(`/admin/eft/transfers/${id}/download`);
      if (data?.url) {
        // The API returns an Azure blob URL rather than streaming a file.
        const link = document.createElement('a');
        link.href = data.url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Downloaded successfully');
      } else {
        toast.error('No download URL returned');
      }
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to start download'));
    } finally {
      toast.dismiss(toastId);
      setIsDownloading(false);
    }
  };

  const handleSaveTransaction = async ({ status: result, reference, transactionDate }) => {
    const toastId = toast.loading('Saving transaction details...');
    try {
      setIsSaving(true);
      await axiosInstance.patch(`/admin/eft/transfers/${id}`, {
        status: result,
        transfer_reference_id: reference || undefined,
        // Already a naive "YYYY-MM-DDT00:00:00" string from the modal; the
        // API defaults to now when it is absent.
        transferred_at: transactionDate || undefined,
      });
      toast.success('Transaction details saved');
      setEditOpen(false);
      fetchDetail();
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to save transaction details'));
    } finally {
      toast.dismiss(toastId);
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteOpen(false);
    const toastId = toast.loading('Deleting transfer...');
    try {
      await axiosInstance.delete(`/admin/eft/transfers/${id}`);
      toast.success(`${detail?.reference_no} deleted. Its payments are eligible again.`);
      navigate('/admin/payments/eft', { state: { activeTab: 1 } });
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to delete transfer'));
    } finally {
      toast.dismiss(toastId);
    }
  };

  // The edit form reads the same row shape the Transferred table passes it.
  const editTarget = detail && {
    id: detail.id,
    referenceNo: detail.reference_no,
    status: detail.status,
    totalAmount: formatAmount(detail.total_amount),
    bankReference: detail.transfer_reference_id || '',
    transferredAtRaw: detail.transferred_at || null,
  };

  return (
    <Layout title="Klass Online | EFT Details">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            <Box className="eftd-breadcrumb">
              Payment {'>'} EFT {'>'} <span className="eftd-breadcrumb-current">Details</span>
            </Box>

            <Box className="eftd-header">
              <Box className="eftd-header-left">
                <button
                  type="button"
                  className="eftd-back-btn"
                  aria-label="Back to EFT list"
                  onClick={() => navigate('/admin/payments/eft', { state: { activeTab: 1 } })}
                >
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </button>
                <span className="eftd-title">{detail?.reference_no || '—'}</span>
                <span
                  className="eft-status-badge"
                  style={{
                    border: `1px solid ${status.color}`,
                    backgroundColor: status.bgcolor,
                    color: status.color,
                    display: 'inline-flex',
                  }}
                >
                  <span
                    className="eft-status-dot"
                    style={{ backgroundColor: status.color, display: 'inline-block' }}
                  />
                  <span className="eft-status-text">{status.label}</span>
                </span>
              </Box>

              <Box className="eftd-header-actions">
                <button
                  type="button"
                  className="eftd-btn eftd-btn-outline"
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  <DownloadIcon sx={{ fontSize: 18 }} />
                  <span>{isDownloading ? 'Preparing...' : 'Download'}</span>
                </button>
                {canDeleteEft && (
                  <button
                    type="button"
                    className="eftd-btn eftd-btn-danger"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    <span>Delete EFT</span>
                  </button>
                )}
                {canManageEft && (
                  <button
                    type="button"
                    className="eftd-btn eftd-btn-primary"
                    onClick={() => setEditOpen(true)}
                  >
                    <img
                      src={TransactionDetailsIcon}
                      alt=""
                      width={18}
                      height={18}
                      style={{ flex: '0 0 auto' }}
                    />
                    <span>
                      {detail?.transfer_reference_id ? 'Edit' : 'Add'} Transaction Details
                    </span>
                  </button>
                )}
              </Box>
            </Box>

            <Box className="eftd-summary-card">
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">EFT ID</Typography>
                <Typography className="eftd-summary-value">
                  {detail?.reference_no || '-'}
                </Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Payment Count</Typography>
                <Typography className="eftd-summary-value">{detail?.item_count ?? '-'}</Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Total Amount (QAR)</Typography>
                <Typography className="eftd-summary-value">
                  {formatAmount(detail?.total_amount)}
                </Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Earliest Payment Date</Typography>
                <Typography className="eftd-summary-value">
                  {formatDate(detail?.earliest_payment_at)}
                </Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Latest Payment Date</Typography>
                <Typography className="eftd-summary-value">
                  {formatDate(detail?.latest_payment_at)}
                </Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Claimed On</Typography>
                <Typography className="eftd-summary-value">
                  {formatDateTime(detail?.created_at)}
                </Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Claimed By</Typography>
                <Typography className="eftd-summary-value">
                  {detail?.claimed_by_name || '-'}
                </Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Transaction ID</Typography>
                <Typography className="eftd-summary-value" sx={{ wordBreak: 'break-all' }}>
                  {detail?.transfer_reference_id || '-'}
                </Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Transaction Date</Typography>
                <Typography className="eftd-summary-value">
                  {detail?.transferred_at ? formatCalendarDate(detail.transferred_at) : '-'}
                </Typography>
              </Box>
              <Box className="eftd-summary-field">
                <Typography className="eftd-summary-label">Transferred By</Typography>
                <Typography className="eftd-summary-value">
                  {detail?.transferred_by_name || '-'}
                </Typography>
              </Box>
            </Box>

            <Box className="eft-card">
              <Box className="eftd-items-header">
                <Typography className="eft-card-title">Included Payments</Typography>
                <Input
                  sx={{
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': { borderBottom: 'none !important' },
                    '&:hover:not(.Mui-disabled):before': { borderBottom: 'none !important' },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': { padding: '3px 5px' },
                    width: '340px',
                    height: '40px',
                  }}
                  type="search"
                  placeholder="Search by Payment ID, Learner or Instructor..."
                  aria-label="Search payments"
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                  startAdornment={<SearchIcon className="eft-search-icon" />}
                />
              </Box>

              {isLoading || isItemsLoading ? (
                <PayoutTableSkeleton />
              ) : (
                <Box className="eft-table-container">
                  <table className="eft-table">
                    <thead>
                      <tr>
                        <th className="eft-th">Payment ID</th>
                        <th className="eft-th">Learner / Instructor</th>
                        <th className="eft-th">Payment For</th>
                        <th className="eft-th">Payment Date</th>
                        <th className="eft-th">Eligible Date</th>
                        <th className="eft-th">Amount (QAR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="eft-empty-cell">
                            <Box className="eft-empty-state">
                              <Typography className="eft-empty-text">
                                No payments match this search
                              </Typography>
                            </Box>
                          </td>
                        </tr>
                      ) : (
                        items.map(item => (
                          <tr key={item.id} className="eft-row">
                            <td className="eft-td">{item.source_payment_id}</td>
                            <td className="eft-td">{item.payer_name || '-'}</td>
                            <td className="eft-td">
                              {SOURCE_LABEL[item.source_type] || item.source_type}
                            </td>
                            <td className="eft-td">{formatDate(item.paid_at)}</td>
                            <td className="eft-td">{formatDate(item.eligible_at)}</td>
                            <td className="eft-td">{formatAmount(item.amount)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Box>
              )}

              <Box className="eft-pagination-container">
                <Box className="eft-pagination-box">
                  <Typography className="eft-pagination-text">Showing</Typography>
                  <Select
                    value={pageSize}
                    onChange={event => {
                      setPageSize(event.target.value);
                      setPage(1);
                    }}
                    sx={{
                      height: '30px',
                      minWidth: '60px',
                      margin: '0 4px',
                      '& .MuiSelect-select': { padding: '6px 24px 6px 8px' },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                  <Typography className="eft-pagination-text">of {itemsCount} payments</Typography>
                </Box>

                {paginationItems.length > 0 && (
                  <nav>
                    <ul className="eft-pagination-list">
                      <li>
                        <button
                          type="button"
                          className={`eft-pagination-btn ${page === 1 ? 'eft-pagination-btn-disabled' : ''}`}
                          onClick={() => setPage(page - 1)}
                          disabled={page === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {paginationItems.map((item, index) => (
                        <li key={`${item}-${index}`}>
                          {item === '...' ? (
                            <span className="eft-pagination-ellipsis">…</span>
                          ) : (
                            <button
                              type="button"
                              className={`eft-pagination-btn ${page === item ? 'eft-pagination-btn-selected' : ''}`}
                              onClick={() => setPage(item)}
                            >
                              {item}
                            </button>
                          )}
                        </li>
                      ))}
                      <li>
                        <button
                          type="button"
                          className={`eft-pagination-btn ${page === totalPages ? 'eft-pagination-btn-disabled' : ''}`}
                          onClick={() => setPage(page + 1)}
                          disabled={page === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <TransactionDetailsModal
        open={editOpen}
        transfer={editTarget}
        isSaving={isSaving}
        onClose={() => setEditOpen(false)}
        onSubmit={handleSaveTransaction}
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle className="eft-dialog-title">Delete {detail?.reference_no}?</DialogTitle>
        <DialogContent>
          <Typography className="eft-dialog-text">
            This removes the transfer and its {detail?.item_count} payments, which return to the
            Eligible tab.
          </Typography>
          <Typography className="eft-dialog-note">
            There is no undo. If the money was already sent, the bank reference and the record of
            what it covered are lost with it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 24px 20px' }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default EFTDetails;
